<template>
  <div
      class="vital"
      :class="[`vital--${slug}`, { 'vital--inline': labels === 'inline', 'is-blank': !hasData, 'is-critical': critical }]"
      :aria-label="`${type} ${current} of ${max}`"
  >
    <span v-if="labels === 'inline'" class="vital__tag">{{ tag }}</span>

    <span v-if="segmented" class="vital__pips">
      <i
          v-for="n in pipTotal"
          :key="n"
          class="vital__pip"
          :class="{ 'is-spent': n > pipFilled }"
      ></i>
    </span>
    <span v-else class="vital__track">
      <span class="vital__fill" :style="{ width: `${percent}%` }"></span>
    </span>

    <span v-if="labels === 'inline'" class="vital__value">{{ current }}<i>/</i>{{ max }}</span>
  </div>
</template>

<script>
const TAGS = {
  Health: "HP",
  Energy: "EN",
  "Action Points": "AP",
};

export default {
  name: "AttributeBar",
  props: {
    current: { type: Number, required: true },
    max: { type: Number, required: true },
    type: { type: String, required: true }, // "Health" | "Energy" | "Action Points"
    labels: { type: String, default: "none" }, // "none" | "inline"
  },
  computed: {
    slug() {
      return this.type.toLowerCase().replace(/\s+/g, "-");
    },
    tag() {
      return TAGS[this.type] || this.type.slice(0, 2).toUpperCase();
    },
    hasData() {
      return Number.isFinite(this.max) && this.max > 0;
    },
    percent() {
      if (!this.hasData) return 0;
      return Math.min(100, Math.max(0, (this.current / this.max) * 100));
    },
    // Action points are a small whole number the GM counts, not a continuous
    // quantity — ticks read the exact value at a glance, a bar never does.
    segmented() {
      return this.type === "Action Points" && this.hasData && this.max <= 12;
    },
    pipTotal() {
      return Math.max(1, Math.round(this.max));
    },
    pipFilled() {
      return Math.min(this.pipTotal, Math.max(0, Math.round(this.current)));
    },
    critical() {
      return this.type === "Health" && this.hasData && this.percent <= 25;
    },
  },
};
</script>

<style scoped>
.vital {
  --vital-h: 0.22rem;
  --vital-lo: #6f2521;
  --vital-hi: #d7574a;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  width: 100%;
}

.vital--energy {
  --vital-lo: #3d6480;
  --vital-hi: #8fbcdd;
}

.vital--action-points {
  --vital-h: 0.2rem;
  --vital-lo: #7d6a37;
  --vital-hi: var(--gm-accent);
}

/* Health carries the most weight of the three — the only one worth a glance
   when five cards are scanned at once. */
.vital--health {
  --vital-h: 0.3rem;
}

.vital__track {
  position: relative;
  flex: 1;
  min-width: 0;
  height: var(--vital-h);
  background: rgba(0, 0, 0, 0.55);
  box-shadow: inset 0 0 0 1px rgba(250, 218, 149, 0.12);
  border-radius: 1px;
  overflow: hidden;
}

.vital__fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, var(--vital-lo), var(--vital-hi));
  transition: width 0.35s ease;
}

.vital__pips {
  display: flex;
  flex: 1;
  min-width: 0;
  gap: 1px;
  height: var(--vital-h);
}

.vital__pip {
  flex: 1;
  min-width: 1px;
  background: var(--vital-hi);
  border-radius: 1px;
  transition: background 0.2s ease;
}

.vital__pip.is-spent {
  background: rgba(0, 0, 0, 0.55);
  box-shadow: inset 0 0 0 1px rgba(250, 218, 149, 0.12);
}

/* No reading yet — a broken line, so it can't be misread as a drained bar. */
.is-blank .vital__track {
  background: repeating-linear-gradient(
      90deg,
      rgba(250, 218, 149, 0.1) 0 2px,
      transparent 2px 6px
  );
  background-size: 100% 1px;
  background-repeat: no-repeat;
  background-position: center;
  box-shadow: none;
}

.is-critical .vital__fill {
  animation: vital-flicker 1.6s ease-in-out infinite;
}

@keyframes vital-flicker {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.45; }
}

@media (prefers-reduced-motion: reduce) {
  .is-critical .vital__fill { animation: none; }
  .vital__fill { transition: none; }
}

/* Inline mode — for cards with room for a readout. */
.vital--inline {
  --vital-h: 0.34rem;
  gap: 0.4rem;
}

.vital__tag,
.vital__value {
  font-family: var(--gm-font-display);
  font-size: 0.55rem;
  letter-spacing: 0.08em;
  line-height: 1;
  white-space: nowrap;
}

.vital__tag {
  width: 1.4rem;
  color: var(--gm-text-dim);
}

.vital__value {
  min-width: 3.4rem;
  text-align: right;
  color: var(--gm-text-muted);
  font-variant-numeric: tabular-nums;
}

.vital__value i {
  font-style: normal;
  margin: 0 0.1em;
  color: var(--gm-text-dim);
}

.is-critical .vital__value {
  color: var(--vital-hi);
}
</style>
